import { Component } from '@angular/core';
import { AuthService, BreadcrumbService, ProductNameService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ProductName, RenderedOutputStatus } from 'situview-webapi-client';
import {
  GetRecipeEventSummaryByArtifactIdRequest, GetRecipeEventSummaryByArtifactIdResponse, RecipeEvent, RecipeEventSummary, RecipeEventType, SituAnalyticsWebApiClient
} from 'situanalytics-webapi-client';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from 'src/app/services/config.service';
import * as moment from 'moment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ArtifactViewerDialogComponent } from 'src/app/components';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'recipe-event-summary',
  templateUrl: './recipe-event-summary.component.html',
  styleUrls: ['./recipe-event-summary.component.scss']
})
export class RecipeEventSummaryComponent extends SituBaseComponent {
  private readonly IdParamName: string = "id";

  id: string = "";
  recipeEventSummary: RecipeEventSummary | undefined;
  recipeEventSummaryGrouped?: RecipeEventSummaryGroupedEvents;
  timelineEvents: TimelineEvent[] | undefined;
  showTimeline: boolean = true;

  productName = ProductName;
  status = RenderedOutputStatus;

  dialogRef: MatDialogRef<ArtifactViewerDialogComponent> | undefined;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private productNameService: ProductNameService,
    private route: ActivatedRoute,
    private router: Router,
    private config: ConfigService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
  ) {
    super(authService, stateService);
    if (this.route.snapshot.params[this.IdParamName]) {
      this.id = this.route.snapshot.params[this.IdParamName];
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadRecipeEventSummary();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stopPollingRenderStatus();
  }

  /* polling */
  pollingInterval: NodeJS.Timeout | null = null;
  pollingFails = 0;

  private startPollingRenderStatus() {
    if (!this.pollingInterval) {
      this.pollingFails = 0;

      this.pollingInterval = setInterval(() => { this.loadRecipeEventSummary(); }, 20000);
    }
  }

  private stopPollingRenderStatus() {
    if (!!this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
  /* polling */

  get progressPercentage(): number {
    if (this.recipeEventSummary?.numberOfFilesRecieved && this.recipeEventSummary?.numberOfFilesExpected) {
      return (this.recipeEventSummary.numberOfFilesRecieved / this.recipeEventSummary.numberOfFilesExpected) * 100;
    }
    return 100;
  }

  toggleTimeline() {
    this.showTimeline = !this.showTimeline;
  }

  viewArtifactFile(item: RecipeEvent) {
    if (!item?.fileName) return;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    this.dialogRef = this.dialog.open(ArtifactViewerDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        fileName: item.fileName,
        artifactId: item.recipeArtifactId
      }
    });

  }

  loadRecipeEventSummary() {
    this.loading = true;
    let request = new GetRecipeEventSummaryByArtifactIdRequest();
    request.artifactId = this.id;
    request.includeSummaries = true;

    return this.situAnalyticsClient.getRecipeEventSummaryByArtifactId(request)
      .then((response: GetRecipeEventSummaryByArtifactIdResponse) => {
        if (response.success && response.recipeEventSummary) {
          this.recipeEventSummary = response.recipeEventSummary;
          let sortedEvents = response.recipeEventSummary.recipeEvents!.sort((a, b) => a.dateTimeStamp!.getTime() - b.dateTimeStamp!.getTime());
          this.recipeEventSummary.recipeEvents = sortedEvents;
          this.timelineEvents = this.getRecipeEventErrors();
          if (this.recipeEventSummary) {

            this.recipeEventSummaryGrouped = new RecipeEventSummaryGroupedEvents(sortedEvents);

            let status = this.getStatusIconAndClass(this.recipeEventSummary);
            //this.updateTimelineChartData();
            if (status?.description === "In Progress") {
              this.startPollingRenderStatus();
            } else {
              this.stopPollingRenderStatus();
            }
          }
        }
      })
      .catch((x: any) => {
        //this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getEventType(item: RecipeEvent): string {
    if (!item?.recipeEventType) return "";
    return RecipeEventType[item.recipeEventType];
  }

  getEventMessage(item: RecipeEvent) {
    return item?.fileName ? item?.fileName : item?.software ? item?.software : item?.event;
  }

  isComplete(recipeEventSummary: RecipeEventSummary) {
    return recipeEventSummary?.dateCompleted;
  }

  share() {
    if (this.id) {
      let productName = this.getProductName(this.recipeEventSummary?.productName!);
      if (!productName) {
        return;
      }
      let url = this.config.config.situLinkBaseUrl ?
        `https://${productName}.${this.config.config.situLinkBaseUrl}/${this.id}` :
        `${this.config.config.situPortalBaseUrl}/${this.id}`;
      this.clipboard.copy(url);
      this.showMessage("Copied situLINK to clipboard!");
    };
  };

  getStatusIconAndClass(recipeEventSummary: RecipeEventSummary) {
    // in progress
    const isInProgress = !recipeEventSummary.dateCancelled && !recipeEventSummary.dateFailed
      && !recipeEventSummary.dateCompleted && !!recipeEventSummary.dateStarted;
    if (isInProgress) {
      return { icon: "pending", class: "text-info", description: "In Progress" };
    }

    // completed
    if (!!recipeEventSummary.dateCompleted) {
      return { icon: "check_circle", class: "text-success", description: "Completed" };
    }

    // failed
    if (!!recipeEventSummary.dateFailed) {
      return { icon: "error", class: "text-danger", description: "Failed" };
    }

    // cancelled
    if (!!recipeEventSummary.dateCancelled) {
      return { icon: "block", class: "text-muted", description: "Cancelled" };
    }

    // queued
    if (!!recipeEventSummary.dateQueued) {
      return { icon: "pending", class: "text-muted", description: "Queued" };
    }
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getRecipeEventErrors() {
    if (!this.recipeEventSummary || !this.recipeEventSummary.recipeEvents) return [];
    let events = new Array<TimelineEvent>();

    let firstJobStart = this.recipeEventSummary.recipeEvents.sort((a, b) => a.dateTimeStamp!.getTime() - b.dateTimeStamp!.getTime()).find(x => x.recipeEventType === 1)
    this.recipeEventSummary.recipeEvents.forEach((x: RecipeEvent) => {
      if (x.recipeEventType === 2) {
        events.push(new TimelineEvent(x.dateTimeStamp!, x.event!, "warning"));
      } else if (x.recipeEventType === 8 && x.software) {
        events.push(new TimelineEvent(x.dateTimeStamp!, `started ${x.software!.replace('software\\', '')}`, "info"));
      } else if (x.recipeEventType === 6) {
        events.push(new TimelineEvent(x.dateTimeStamp!, "Queued", "success"));
      }
    });
    if (this.recipeEventSummary.dateCompleted) {
      events.push(new TimelineEvent(this.recipeEventSummary.dateCompleted, "Completed", "success"));
    }
    if (this.recipeEventSummary.dateAdded) {
      events.push(new TimelineEvent(firstJobStart?.dateTimeStamp!, "Started", "success"));
    }

    let sortedEvents = events.sort((a, b) => a.eventDateTime.getTime() - b.eventDateTime.getTime());
    return sortedEvents;
  }

  getTimeToNextEvent(events: TimelineEvent[], eventIndex: number): string {
    let currentEvent = events[eventIndex];
    let returnString = "";

    let nextEventIndex = eventIndex + 1;
    if (nextEventIndex >= events.length) {
      return returnString;
    }
    let nextEvent = events[nextEventIndex];

    if (currentEvent?.eventDateTime && nextEvent?.eventDateTime) {
      let diff = moment(nextEvent.eventDateTime).diff(moment(currentEvent.eventDateTime));
      let duration = moment.duration(diff);

      var hours = duration.hours();
      if (hours) {
        returnString += `${hours}h `;
      }

      var minutes = duration.minutes();
      if (minutes) {
        returnString += `${minutes}m `;
      }

      var seconds = duration.seconds();
      if (seconds) {
        returnString += `${seconds}s `;
      }

      var milliseconds = duration.milliseconds();
      if (!hours && !minutes) {
        returnString += `${milliseconds}ms `;
      }
    }

    return returnString;
  }

  getTimeToNextRecipeEvent(events: RecipeEvent[], eventIndex: number): string {
    let currentEvent = events[eventIndex];

    let nextEventIndex = eventIndex + 1;
    if (nextEventIndex >= events.length) {
      return '';
    }
    let nextEvent = events[nextEventIndex];

    let returnString = getTimeBetweenEvents(currentEvent, nextEvent);

    return returnString;
  }

  getSeqUrl(value: string, isCorrelationId: boolean): string {
    const envName = this.config.config.envName;
    let seqUrl: URL;

    if (envName === 'dev') {
      seqUrl = new URL('http://seq.situflow.com:5341');
    } else {
      seqUrl = new URL('https://seq.situsystems.com');
    }
    seqUrl.pathname = 'events';
    seqUrl.searchParams.set('filter', isCorrelationId ? `CorrelationId = '${value}'` : `ArtifactId = '${value}'`);
    if (envName === 'Staging') seqUrl.searchParams.set('signal', 'signal-26');
    if (envName === 'Production') seqUrl.searchParams.set('signal', 'signal-28');

    return seqUrl.toString().replace('/events', '#/events');
  }

  navigateToRenderedOutput(renderedOutId: string, productName: ProductName) {
    let url = '';

    switch (productName) {
      case ProductName.Facade:
        url = this.router.serializeUrl(this.router.createUrlTree(["/situ-facade"], { queryParams: { id: renderedOutId } }));
        break;
      case ProductName.Panorama:
      case ProductName.RayTraced:
      case ProductName.RayTracedPlus:
      case ProductName.Insights:
      case ProductName.Plan:
      case ProductName.VR:
      case ProductName.Render:
        url = this.router.serializeUrl(this.router.createUrlTree(["/situ-view"], { queryParams: { id: renderedOutId } }));
        break;
    }

    if (url) {
      window.open(url, '_blank');
    }
  }

  isOlderThan2Minutes(date: Date) {
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000); // 2 minutes in milliseconds
    return date < twoMinutesAgo;
  }

  calculateWidth(stage: StageKey): number {
    if (!this.recipeEventSummary || !this.recipeEventSummary.totalTimeInSeconds) {
      return 0;
    }
    let stageTime = this.recipeEventSummary[stage] ?? 0;

    if (stage !== 'totalSecondsInQueue') {
      const previousStageTime = this.getPreviousStageTime(stage);
      stageTime -= previousStageTime;
    }

    return (stageTime / this.recipeEventSummary.totalTimeInSeconds) * 100;
  }

  getPreviousStageTime(stage: StageKey): number {
    switch (stage) {
      case 'secondsFromStartToFirstFileFound':
        return this.recipeEventSummary?.totalSecondsInQueue ?? 0;
      case 'secondsFromStartToFirstFileAdded':
        return this.recipeEventSummary?.secondsFromStartToFirstFileFound ?? 0;
      case 'secondsFromStartToFloorplanFilesAdded':
        return this.recipeEventSummary?.secondsFromStartToFirstFileAdded ?? 0;
      case 'secondsFromStartToFirstPanoramaAdded':
        return this.recipeEventSummary?.secondsFromStartToFloorplanFilesAdded ?? 0;
      case 'totalSecondsAtRenderer':
        return this.recipeEventSummary?.secondsFromStartToFirstPanoramaAdded ?? 0;
      default:
        return 0;
    }
  }

  isLink(item: RecipeEvent): boolean {
    return (item?.dateTimeStamp &&
      item?.recipeEventType === 5 &&
      (item?.fileName?.endsWith('.jpg') || item?.fileName?.endsWith('.webp') || item?.fileName?.endsWith('.glb'))) || false;
  }

  getProductName(productName: ProductName) {
    if (productName === undefined) return null;
    switch (productName) {
      case ProductName.Panorama:
      case ProductName.RayTraced:
      case ProductName.RayTracedPlus:
      case ProductName.Insights:
        return "view";
      case ProductName.Plan:
        return "plan";
      case ProductName.Render:
        return "render";
      case ProductName.Facade:
        return "facade";
      case ProductName.ProductPreview:
        return "product";
      case ProductName.Connect:
        return "connect";
      default:
        return null;
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class TimelineEvent {
  constructor(
    public eventDateTime: Date,
    public eventMessage: string,
    public eventTypeStyle: string) {
  }
}

type StageKey = 'totalSecondsInQueue' | 'secondsFromStartToFirstFileFound' | 'secondsFromStartToFirstFileAdded' | 'secondsFromStartToFloorplanFilesAdded' | 'secondsFromStartToFirstPanoramaAdded' | 'totalSecondsAtRenderer';

function getTimeBetweenEvents(prevEvent: RecipeEvent | undefined | null, nextEvent: RecipeEvent | undefined | null) {
  let returnString = "";

  if (prevEvent?.dateTimeStamp && nextEvent?.dateTimeStamp) {
    let diff = moment(nextEvent.dateTimeStamp).diff(moment(prevEvent.dateTimeStamp), 'ms');
    let duration = moment.duration(diff, 'ms');

    const totalSeconds = diff / 1000;
    const totalMinutes = totalSeconds / 60;
    const totalHours = totalMinutes / 60;

    var hours = duration.hours();
    if (hours) {
      returnString += `${hours}h `;
    }

    var minutes = duration.minutes();
    if (minutes) {
      returnString += `${minutes}m `;
    }

    if (totalMinutes < 30) {
      var seconds = duration.seconds();
      if (seconds) {
        returnString += `${seconds}s `;
      }
    }

    if (totalSeconds < 1) {
      var milliseconds = duration.milliseconds();
      if (!hours && !minutes) {
        returnString += `${milliseconds}ms `;
      }
    }
  }
  return returnString;
}


interface RecipeEventSummaryGroupOfEvents {

  dateTimeStamp: Date;

  timeSinceLastGroup: string;

  events: RecipeEvent[];
}


class RecipeEventSummaryGroupedEvents {

  public groups: RecipeEventSummaryGroupOfEvents[];
  constructor(private events: RecipeEvent[]) {
    const groupedEvents = groupCloseEvents(events, 5, 20);

    this.groups = [];

    for (const srcGroup of groupedEvents) {
      if (srcGroup.length === 0) continue;

      const prevGroup = this.groups.length > 0 ? this.groups[this.groups.length - 1] : null;

      const newGroup: RecipeEventSummaryGroupOfEvents = {
        events: srcGroup,
        dateTimeStamp: srcGroup[0].dateTimeStamp ?? new Date(),
        timeSinceLastGroup: getTimeBetweenEvents(prevGroup?.events[0], srcGroup[0]),
      }

      this.groups.push(newGroup)
    }

    if (this.groups.length > 0) {
      const lastGroup = this.groups[this.groups.length - 1];

      if (lastGroup.events.length > 0) {
        const firstEvent = lastGroup.events[0];
        const lastEvent = lastGroup.events[lastGroup.events.length - 1];

        const timeDiff = getTimeBetweenEvents(firstEvent, lastEvent);

        this.groups.push({
          dateTimeStamp: lastEvent.dateTimeStamp ?? new Date(),
          events: [],
          timeSinceLastGroup: timeDiff,
        })
      }
    }


  }



}


function groupCloseEvents(events: RecipeEvent[], maxGapSeconds: number, maxGroupSeconds: number): RecipeEvent[][] {
  const groups: RecipeEvent[][] = [];
  let currentGroup: RecipeEvent[] = [];

  for (let i = 0; i < events.length; i++) {
    const current = events[i];

    if (!current.dateTimeStamp) {
      continue; // Skip events without timestamps
    }

    if (currentGroup.length === 0) {
      currentGroup.push(current);
    } else {
      const first = currentGroup[0];
      const previous = currentGroup[currentGroup.length - 1];
      if (!previous.dateTimeStamp || !first.dateTimeStamp) {
        continue;
      }

      const diffToLastEventMilliseconds = current.dateTimeStamp.getTime() - previous.dateTimeStamp.getTime();
      const diffToLastEventSeconds = diffToLastEventMilliseconds / 1000;

      const diffToFirstGroupedEventMilliseconds = current.dateTimeStamp.getTime() - first.dateTimeStamp.getTime();
      const diffToFirstGroupedEventSeconds = diffToFirstGroupedEventMilliseconds / 1000;


      if (diffToLastEventSeconds <= maxGapSeconds && diffToFirstGroupedEventSeconds < maxGroupSeconds) {
        currentGroup.push(current);
      } else {
        groups.push(currentGroup);
        currentGroup = [current];
      }
    }
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}
